import { RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AgentClientType, IAgentClientRow } from '../../../../store/users/usersApi';

const tableColumns = [
    'Company',
    'Email',
    'Username',
    'Client ID',
    'Agents',
    'Status',
    'Actions',
];

const agentBadgeClassMap: Record<AgentClientType, string> = {
    sofia: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700',
    leo: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700',
    charles: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700',
    claire: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700',
    rebecca: 'bg-pink-100 text-pink-700 border-pink-200 dark:bg-pink-900/40 dark:text-pink-300 dark:border-pink-700',
    filipa: 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/40 dark:text-cyan-300 dark:border-cyan-700',
    iris: 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700',
    martin: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700',
};

const formatText = (value?: string) => {
    if (!value) return '--';
    return value;
};

const toAgentLabel = (agent?: string | null) =>
    agent ? `${agent.charAt(0).toUpperCase()}${agent.slice(1)}` : '--';

const AgentBadges = ({ agents }: { agents: AgentClientType[] }) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
    const visibleAgents = agents.slice(0, 2);
    const hiddenAgentsCount = agents.length - visibleAgents.length;

    useEffect(() => {
        if (!showTooltip) return undefined;

        const updateTooltipPosition = () => {
            const trigger = triggerRef.current;
            if (!trigger) return;

            const rect = trigger.getBoundingClientRect();
            const tooltipWidth = 256;
            const estimatedTooltipHeight = 68 + (agents.length * 34);
            const left = Math.min(
                Math.max(12, rect.left),
                Math.max(12, window.innerWidth - tooltipWidth - 12),
            );
            const belowTrigger = rect.bottom + 8;
            const top = belowTrigger + estimatedTooltipHeight <= window.innerHeight - 12
                ? belowTrigger
                : Math.max(12, rect.top - estimatedTooltipHeight - 8);

            setTooltipPosition({ top, left });
        };

        updateTooltipPosition();
        window.addEventListener('resize', updateTooltipPosition);
        window.addEventListener('scroll', updateTooltipPosition, true);

        return () => {
            window.removeEventListener('resize', updateTooltipPosition);
            window.removeEventListener('scroll', updateTooltipPosition, true);
        };
    }, [agents.length, showTooltip]);

    if (agents.length === 0) return <span>--</span>;

    return (
        <>
            <div
                ref={triggerRef}
                tabIndex={0}
                aria-label={`Assigned agents: ${agents.map(toAgentLabel).join(', ')}`}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onFocus={() => setShowTooltip(true)}
                onBlur={() => setShowTooltip(false)}
                className='inline-flex max-w-[250px] flex-wrap items-center gap-1.5 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-black'
            >
                {visibleAgents.map((agent) => (
                    <span
                        key={agent}
                        className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${agentBadgeClassMap[agent]}`}
                    >
                        {toAgentLabel(agent)}
                    </span>
                ))}
                {hiddenAgentsCount > 0 && (
                    <span className='inline-flex rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'>
                        +{hiddenAgentsCount}
                    </span>
                )}
            </div>

            {showTooltip && tooltipPosition && typeof document !== 'undefined' && createPortal(
                <div
                    role='tooltip'
                    style={tooltipPosition}
                    className='pointer-events-none fixed z-[100] w-64 overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-2xl shadow-slate-900/20 dark:border-emerald-900/70 dark:bg-slate-950'
                >
                    <div className='flex items-center justify-between bg-gradient-to-r from-[#00330f] to-emerald-700 px-4 py-3 text-white'>
                        <div>
                            <p className='text-xs font-semibold uppercase tracking-wider text-emerald-100'>
                                Agent access
                            </p>
                            <p className='mt-0.5 text-sm font-bold'>All assigned agents</p>
                        </div>
                        <span className='flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-sm font-bold ring-1 ring-white/25'>
                            {agents.length}
                        </span>
                    </div>
                    <div className='grid grid-cols-2 gap-2 p-3'>
                        {agents.map((agent) => (
                            <div
                                key={agent}
                                className={`flex items-center gap-2 rounded-xl border px-2.5 py-2 text-xs font-semibold ${agentBadgeClassMap[agent]}`}
                            >
                                <span className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/70 text-[10px] shadow-sm dark:bg-black/25'>
                                    &#10003;
                                </span>
                                <span>{toAgentLabel(agent)}</span>
                            </div>
                        ))}
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
};

const AgentClientsTable = ({
    rows,
    loading,
    loadingMore,
    hasMore,
    onLoadMore,
    onResetPassword,
    scrollContainerRef,
}: {
    rows?: IAgentClientRow[];
    loading?: boolean;
    loadingMore?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
    onResetPassword?: (row: IAgentClientRow) => void;
    scrollContainerRef?: RefObject<HTMLDivElement>;
}) => {
    const handleScroll = () => {
        if (!scrollContainerRef?.current || !hasMore || loadingMore || loading) return;
        const el = scrollContainerRef.current;
        if (el.scrollHeight <= el.clientHeight) return;
        if (el.scrollTop <= 0) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
            onLoadMore?.();
        }
    };

    return (
        <div className='overflow-x-auto border border-1 rounded-xl dark:border-borderColor-darkMode'>
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className='overflow-y-auto'
                style={{ maxHeight: '650px' }}
            >
                <table className='min-w-full bg-white dark:bg-black dark:text-white min-h-[220px]'>
                    <thead className='sticky top-0 z-10 bg-white dark:bg-black text-left text-sm font-medium text-black dark:text-white'>
                        <tr>
                            {tableColumns.map((column) => (
                                <th key={column} className='px-4 py-3 min-w-[180px]'>
                                    {column}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {loading && (!rows || rows.length === 0)
                            && Array.from({ length: 8 }).map((_, i) => (
                                <tr key={`agent-clients-skeleton-${i}`} className='animate-pulse border-b'>
                                    {Array.from({ length: tableColumns.length }).map((__, j) => (
                                        <td key={`agent-clients-skeleton-cell-${i}-${j}`} className='px-4 py-4'>
                                            <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4' />
                                        </td>
                                    ))}
                                </tr>
                            ))}

                        {!loading && (!rows || rows.length === 0) && (
                            <tr>
                                <td colSpan={tableColumns.length} className='px-4 py-8 text-center text-gray-500 dark:text-gray-400'>
                                    No agent clients currently available
                                </td>
                            </tr>
                        )}

                        {rows && rows.length > 0 && rows.map((row, idx) => (
                            <tr
                                key={`${row?.id || row?.clientId || 'agent-client'}-${idx}`}
                                className='hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm border-b'
                            >
                                <td className='px-4 py-4'>
                                    <div className='flex flex-col'>
                                        <span className='font-medium'>{formatText(row.companyName)}</span>
                                        <span className='text-xs text-gray-500 dark:text-gray-400'>
                                            {formatText(row.name)}
                                        </span>
                                    </div>
                                </td>
                                <td className='px-4 py-4'>
                                    <p className='max-w-[280px] break-all' title={row.email}>
                                        {formatText(row.email)}
                                    </p>
                                </td>
                                <td className='px-4 py-4'>
                                    <p className='max-w-[240px] break-words' title={row.username}>
                                        {formatText(row.username)}
                                    </p>
                                </td>
                                <td className='px-4 py-4'>
                                    <p className='max-w-[280px] break-all text-xs text-gray-600 dark:text-gray-300' title={row.clientId || row.id}>
                                        {formatText(row.clientId || row.id)}
                                    </p>
                                </td>
                                <td className='px-4 py-4'>
                                    <AgentBadges agents={row.agents} />
                                </td>
                                <td className='px-4 py-4'>
                                    {typeof row.enabled === 'boolean' ? (
                                        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                                            row.enabled
                                                ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700'
                                                : 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900/40 dark:text-gray-300 dark:border-gray-700'
                                        }`}>
                                            {row.enabled ? 'Enabled' : 'Disabled'}
                                        </span>
                                    ) : (
                                        <span>--</span>
                                    )}
                                </td>
                                <td className='px-4 py-4'>
                                    <button
                                        type='button'
                                        disabled={!row.username || row.username === '--'}
                                        onClick={() => onResetPassword?.(row)}
                                        className='rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:border-borderColor-darkMode dark:text-primary-darkMode hover:bg-gray-50 dark:hover:bg-bgColor-darkMode disabled:cursor-not-allowed disabled:opacity-50'
                                    >
                                        Reset Password
                                    </button>
                                </td>
                            </tr>
                        ))}

                        {loadingMore && rows && rows.length > 0 && (
                            <tr>
                                <td colSpan={tableColumns.length} className='px-4 py-5'>
                                    <div className='flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400'>
                                        <svg className='h-5 w-5 animate-spin text-gray-400' viewBox='0 0 24 24' fill='none'>
                                            <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                            <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4A4 4 0 008 12H4z' />
                                        </svg>
                                        <span>Loading more agent clients...</span>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!loading && !loadingMore && rows && rows.length > 0 && hasMore === false && (
                            <tr>
                                <td colSpan={tableColumns.length} className='px-4 py-4 text-center text-xs text-gray-400'>
                                    All agent clients loaded
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AgentClientsTable;
